/**
 * FirewallForm — Odoo-style full-screen detail view for physical firewalls.
 * Tabs: General / Interfaces / IPAM
 * SmartButtons: Vendor, Appliance, Interfaces, HA/VPN, Estado
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  WifiIcon,
  LockClosedIcon,
  PlusIcon,
  PencilSquareIcon,
  SparklesIcon,
  MapPinIcon,
  ServerStackIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { Modal, Button, Form as BsForm } from 'react-bootstrap';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import ElementIpPanel from './ElementIpPanel';
import LocationTreeSelect from './common/LocationTreeSelect';
import DeviceAccessPanel from './DeviceAccessPanel';
import NetworkConnectionsPanel from './NetworkConnectionsPanel';
import PowerConnectionsPanel from './PowerConnectionsPanel';
import SwitchPsuSection from './SwitchPsuSection';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import FormCardsShell, { Sf2Card } from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import '../styles/DetailView.css';
import './ServerFormCards.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';

// ── Constants ────────────────────────────────────────────────────────────────

const APPLIANCES = [
  { value: 'none',       label: 'Ninguno (firmware propietario)' },
  { value: 'pfsense',    label: 'pfSense' },
  { value: 'opnsense',   label: 'OPNsense' },
  { value: 'vyos',       label: 'VyOS' },
  { value: 'ipfire',     label: 'IPFire' },
  { value: 'smoothwall', label: 'Smoothwall' },
  { value: 'untangle',   label: 'Untangle' },
  { value: 'zeroshell',  label: 'Zeroshell' },
  { value: 'other',      label: 'Otro' },
];

const IFACE_TYPES = [
  { value: 'wan',     label: 'WAN' },
  { value: 'lan',     label: 'LAN' },
  { value: 'dmz',     label: 'DMZ' },
  { value: 'mgmt',    label: 'Management' },
  { value: 'ha',      label: 'HA / Heartbeat' },
  { value: 'vpn',     label: 'VPN' },
  { value: 'transit', label: 'Transit / P2P' },
  { value: 'other',   label: 'Otro' },
];

const IFACE_TYPE_COLOR = {
  wan:     { bg: '#fef3c7', color: '#92400e' },
  lan:     { bg: '#dcfce7', color: '#15803d' },
  dmz:     { bg: '#fce7f3', color: '#9d174d' },
  mgmt:    { bg: '#e0f2fe', color: '#0369a1' },
  ha:      { bg: '#ede9fe', color: '#5b21b6' },
  vpn:     { bg: '#f0fdf4', color: '#166534' },
  transit: { bg: '#fff7ed', color: '#9a3412' },
  other:   { bg: '#f1f5f9', color: '#475569' },
};

/* Tipo FÍSICO del puerto (medio/conector), distinto del rol lógico de IFACE_TYPES.
   Mismos valores que los puertos de switch. */
const IFACE_CONNECTORS = [
  { value: '',        label: '— sin especificar —' },
  { value: 'rj45',    label: 'RJ45 (cobre)' },
  { value: 'sfp',     label: 'SFP (1G)' },
  { value: 'sfp+',    label: 'SFP+ (10G)' },
  { value: 'sfp28',   label: 'SFP28 (25G)' },
  { value: 'qsfp',    label: 'QSFP (40G)' },
  { value: 'qsfp28',  label: 'QSFP28 (100G)' },
  { value: 'lc',      label: 'LC (fibra)' },
  { value: 'sc',      label: 'SC (fibra)' },
  { value: 'dac',     label: 'DAC (cable directo)' },
  { value: 'other',   label: 'Otro' },
];

const IFACE_CONNECTOR_SHORT = {
  rj45: 'RJ45', sfp: 'SFP', 'sfp+': 'SFP+', sfp28: 'SFP28',
  qsfp: 'QSFP', qsfp28: 'QSFP28', lc: 'LC', sc: 'SC', dac: 'DAC', other: 'Otro',
};

const DEPLOYMENT_TYPES = [
  { value: 'physical', label: 'Físico' },
  { value: 'virtual',  label: 'Virtual (VM)' },
];

const STATUS_COLORS = {
  active:      { bg: '#dcfce7', color: '#15803d', label: 'Activo' },
  standby:     { bg: '#e0f2fe', color: '#0369a1', label: 'Standby' },
  maintenance: { bg: '#fef9c3', color: '#854d0e', label: 'Mantenimiento' },
  decom:       { bg: '#fee2e2', color: '#991b1b', label: 'Decom' },
};

const EMPTY_FORM = {
  name: '', hostname: '', vendor: 'generic', model: '',
  serial_number: '', asset_tag: '', firmware_version: '',
  appliance_type: 'none', appliance_version: '',
  ha_enabled: false, ha_role: 'standalone',
  ids_ips: false, vpn_capable: false, throughput_gbps: '',
  status: 'active', notes: '',
  deployment_type: 'physical', vm_id: '',
  // Physical placement
  form_factor: 'rack',
  location_id: '', location_name: '',
  rack_id: '', rack_name: '',
  size_u: 1, position_u: '',
};

const EMPTY_IFACE = {
  name: '', iface_type: 'lan', description: '', mac_address: '',
  speed_mbps: '', vlan_id: '', connector_type: '', enabled: true, sort_order: 0,
};

/** Vuelca un firewall del API en el estado del formulario. Está fuera del
 *  componente porque lo usan dos cosas: la carga inicial y «Descartar». */
function formFromFirewall(fw) {
  return {
    name:              fw.name || '',
    hostname:          fw.hostname || '',
    vendor:            fw.vendor || 'generic',
    model:             fw.model || '',
    serial_number:     fw.serial_number || '',
    asset_tag:         fw.asset_tag || '',
    firmware_version:  fw.firmware_version || '',
    appliance_type:    fw.appliance_type || 'none',
    appliance_version: fw.appliance_version || '',
    ha_enabled:        !!fw.ha_enabled,
    ha_role:           fw.ha_role || 'standalone',
    ids_ips:           !!fw.ids_ips,
    vpn_capable:       !!fw.vpn_capable,
    throughput_gbps:   fw.throughput_gbps ?? '',
    status:            fw.status || 'active',
    notes:             fw.notes || '',
    deployment_type:   fw.deployment_type || 'physical',
    vm_id:             fw.vm_id || '',
    // Physical placement
    form_factor:   fw.form_factor  || 'rack',
    location_id:   fw.location_id  ?? '',
    location_name: fw.location_name || '',
    rack_id:       fw.rack_id       ?? '',
    rack_name:     fw.rack_name     || '',
    size_u:        fw.size_u        ?? 1,
    position_u:    fw.position_u    ?? '',
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function FirewallForm({
  firewall,
  companyId,
  onClose,
  onSave,
  /** `'view'` abre la ficha en LECTURA (clic en la fila del listado). */
  initialMode = 'edit',
  /** `true` abre el diálogo de clonado nada más entrar (botón Copiar del listado). */
  startClone = false,
  /** `'cards'` = variante tarjetas (estándar del CMDB). La clásica no cambia. */
  variant = 'classic',
  navItems = [],
  navIndex = -1,
  onNavigate,
}) {
  const isCards = variant === 'cards';
  /* Pasar de ficha a ficha sin salir al listado (§12.A5). */
  const irAFirewall = (destino) => {
    if (!destino || saving) return;
    navGuard.request(() => {
      setIsDirty(false);
      setError(null);
      onNavigate?.(destino);
    });
  };
  const isEditing = Boolean(firewall?.id);
  const [editing, setEditing] = useState(initialMode !== 'view');

  const [form,    setForm]    = useState(EMPTY_FORM);
  const [ifaces,  setIfaces]  = useState([]);
  // En tarjetas la primera pestaña es Resumen (el contenido de «General»).
  const [tab,     setTab]     = useState('general');
  const [accessCount, setAccessCount] = useState(0);
  const [saving,  setSaving]  = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [error,   setError]   = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'este firewall',
    actionLabel: 'pasas a otro firewall o cierras la ficha',
    save: () => handleSave({ keepEditing: editing }),
  });
  const [suggesting, setSuggesting] = useState(false);

  // Interface management
  const [showIfaceForm, setShowIfaceForm]   = useState(false);
  const [ifaceForm,     setIfaceForm]       = useState(EMPTY_IFACE);
  const [editingIface,  setEditingIface]    = useState(null);
  const [ifaceSaving,   setIfaceSaving]     = useState(false);
  const [ifaceError,    setIfaceError]      = useState(null);

  // VMs for virtual firewall link
  const [vmOptions, setVmOptions] = useState([]);

  // Physical placement
  const [locations,     setLocations]     = useState([]);
  const [racks,         setRacks]         = useState([]);
  const [rackFreeUnits, setRackFreeUnits] = useState(null);

  // Catalog dropdowns
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [modelOptions,        setModelOptions]        = useState([]);

  // Alta de marca / modelo en el maestro de la empresa
  const [addingManufacturer, setAddingManufacturer] = useState(false);
  const [newManufacturer,    setNewManufacturer]    = useState('');
  const [addingModel,        setAddingModel]        = useState(false);
  const [newModel,           setNewModel]           = useState('');
  const [catalogError,       setCatalogError]       = useState(null);
  const [catalogSaving,      setCatalogSaving]      = useState(false);

  // Clonado del firewall
  const [showCloneModal, setShowCloneModal] = useState(Boolean(startClone));
  const [cloneName,      setCloneName]      = useState('');
  const [cloning,        setCloning]        = useState(false);
  const [cloneError,     setCloneError]     = useState(null);

  // ── Load VMs for virtual firewall ──────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    api.getVMs(companyId).then(r => {
      setVmOptions(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setVmOptions([]));
  }, [companyId]);

  // ── Load locations and racks ───────────────────────────────────────────────
  const loadAux = useCallback(async () => {
    if (!companyId) return;
    try {
      const [locRes, rackRes, mfrRes, modRes] = await Promise.all([
        // getLocations(query, companyId): el company_id va en el 2º argumento
        api.getLocations('', companyId).catch(() => ({ data: [] })),
        api.getRacks(null, companyId).catch(() => ({ data: [] })),
        api.getManufacturers(companyId, { deviceType: 'network' }).catch(() => ({ data: [] })),
        api.getDeviceModels(companyId,  { deviceType: 'network' }).catch(() => ({ data: [] })),
      ]);
      setLocations(locRes.data || []);
      setRacks(rackRes.data || []);
      setManufacturerOptions(Array.isArray(mfrRes?.data) ? mfrRes.data : []);
      setModelOptions(        Array.isArray(modRes?.data) ? modRes.data : []);
    } catch (_) {}
  }, [companyId]);

  useEffect(() => { loadAux(); }, [loadAux]);

  // When editing an existing physical firewall that already has a rack, load its free units
  useEffect(() => {
    if (!firewall?.rack_id || !companyId) return;
    if (firewall.location_id) {
      api.getRacks(firewall.location_id, companyId).then(r => setRacks(r.data || [])).catch(() => {});
    }
    api.getRack(firewall.rack_id, companyId)
      .then(res => setRackFreeUnits(res.data?.available_units_front || []))
      .catch(() => {});
  }, [firewall?.rack_id, firewall?.location_id, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Bootstrap ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (firewall) {
      setForm(formFromFirewall(firewall));
      setIfaces(firewall.interfaces || []);
    } else {
      setForm(EMPTY_FORM);
      setIfaces([]);
    }
    setIsDirty(false);
  }, [firewall]);

  const sf = (k, v) => { setForm(f => ({ ...f, [k]: v })); setIsDirty(true); };

  /** Descartar: en una ficha ya guardada se recuperan sus valores y vuelve a
   *  LECTURA (no la cierra); en un alta sin guardar, se cierra. */
  const handleDiscard = () => {
    if (isEditing) {
      setForm(formFromFirewall(firewall));
      setError(null);
      setIsDirty(false);
      setEditing(false);
    } else {
      onClose?.();
    }
  };

  // Copiar desde el listado: el diálogo de clonado ya está abierto, pero el
  // nombre sugerido no se puede poner hasta que el formulario tiene el firewall.
  useEffect(() => {
    if (!startClone || !isEditing || cloneName) return;
    setCloneName(`${firewall?.name || ''} (copia)`);
  }, [startClone, isEditing, firewall?.name, cloneName]);

  // ── Location / rack handlers ───────────────────────────────────────────────
  const handleLocationChange = async (e) => {
    const lid = e.target.value;
    const loc = locations.find(l => String(l.id) === String(lid));
    setForm(f => ({
      ...f,
      location_id:   lid,
      location_name: loc?.name || '',
      rack_id:       '',
      rack_name:     '',
      position_u:    '',
    }));
    setIsDirty(true);
    setRackFreeUnits(null);
    try {
      const res = await api.getRacks(lid || null, companyId);
      setRacks(res.data || []);
    } catch (_) {}
  };

  const handleRackChange = async (e) => {
    const rid = e.target.value;
    const rack = racks.find(r => String(r.id) === String(rid));
    setForm(f => ({
      ...f,
      rack_id:       rid,
      rack_name:     rack?.name || '',
      location_id:   rack?.location_id ? String(rack.location_id) : f.location_id,
      location_name: rack?.location_name || f.location_name,
      position_u:    '',
    }));
    setIsDirty(true);
    setRackFreeUnits(null);
    if (rid) {
      try {
        const res = await api.getRack(rid, companyId);
        setRackFreeUnits(res.data?.available_units_front || []);
      } catch (_) {}
    }
  };

  // Si hay sede elegida, solo se ofrecen los racks de esa sede
  const filteredRacks = form.location_id
    ? racks.filter(r => String(r.location_id) === String(form.location_id))
    : racks;

  const rangeLabel = useMemo(() => {
    const posVal  = Number(form.position_u);
    const sizeVal = Number(form.size_u);
    if (!posVal || !sizeVal) return null;
    return `U${posVal} – U${posVal + sizeVal - 1}`;
  }, [form.position_u, form.size_u]);

  const validPositions = useMemo(() => {
    if (rackFreeUnits === null) return null;
    const sizeVal = Number(form.size_u) || 1;
    const freeSet = new Set(rackFreeUnits);
    const currentU = Number(form.position_u);
    if (currentU) {
      for (let u = currentU; u < currentU + sizeVal; u++) freeSet.add(u);
    }
    return [...freeSet].sort((a, b) => a - b).filter(u => {
      for (let i = 0; i < sizeVal; i++) {
        if (!freeSet.has(u + i)) return false;
      }
      return true;
    });
  }, [rackFreeUnits, form.size_u, form.position_u]);

  const handleSuggest = async () => {
    if (!companyId) return;
    setSuggesting(true);
    try {
      const resp = await api.suggestName({ type: 'firewall', companyId, consume: true });
      const s = resp?.data?.suggested_name;
      if (s) sf('name', s);
    } catch (_) {}
    finally { setSuggesting(false); }
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async ({ keepEditing = false } = {}) => {
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return false; }
    try {
      setSaving(true); setError(null);
      const payload = {
        ...form,
        throughput_gbps: form.throughput_gbps !== '' ? Number(form.throughput_gbps) : null,
        vm_id: form.deployment_type === 'virtual' && form.vm_id ? Number(form.vm_id) : null,
        ...(form.deployment_type !== 'virtual' ? {
          location_id: form.location_id !== '' ? Number(form.location_id) : null,
          rack_id:     form.rack_id     !== '' ? Number(form.rack_id)     : null,
          position_u:  form.position_u  !== '' ? Number(form.position_u)  : null,
          size_u:      Number(form.size_u || 1),
          form_factor: form.form_factor,
        } : {}),
      };
      let creado = null;
      if (isEditing) await api.updateFirewall(firewall.id, payload, companyId);
      else           creado = registroGuardado(await api.createFirewall(payload, companyId));
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
      setIsDirty(false);
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las pestañas que necesitan estar guardado.
      onSave?.(creado);
      // Guardar una ficha que ya existía vuelve a lectura… salvo que se esté
      // pasando a la siguiente con «Guardar y continuar».
      if (isEditing && !keepEditing) setEditing(false);
      return true;
    } catch (e) { setError(e.message); return false; }
    finally { setSaving(false); }
  };

  // ── Clonado ────────────────────────────────────────────────────────────────
  const openCloneModal = () => {
    setCloneName(`${form.name || firewall?.name || ''} (copia)`);
    setCloneError(null);
    setShowCloneModal(true);
  };

  const handleCloneConfirm = async () => {
    if (!cloneName.trim() || cloning) return;
    try {
      setCloning(true); setCloneError(null);
      await api.cloneFirewall(firewall.id, cloneName.trim(), companyId);
      setShowCloneModal(false);
      // Se vuelve a la lista para que el clon recién creado quede a la vista
      onClose?.();
      onSave?.();
    } catch (e) { setCloneError(e.message); }
    finally { setCloning(false); }
  };

  // ── Interface handlers ─────────────────────────────────────────────────────
  const openNewIface = () => {
    setEditingIface(null);
    setIfaceForm(EMPTY_IFACE);
    setIfaceError(null);
    setShowIfaceForm(true);
  };

  const openEditIface = (iface) => {
    setEditingIface(iface);
    setIfaceForm({
      name:        iface.name || '',
      iface_type:  iface.iface_type || 'lan',
      description: iface.description || '',
      mac_address: iface.mac_address || '',
      speed_mbps:  iface.speed_mbps ?? '',
      vlan_id:     iface.vlan_id ?? '',
      connector_type: iface.connector_type || '',
      enabled:     iface.enabled !== false,
      sort_order:  iface.sort_order ?? 0,
    });
    setIfaceError(null);
    setShowIfaceForm(true);
  };

  const handleIfaceSave = async () => {
    if (!ifaceForm.name.trim()) { setIfaceError('El nombre del interfaz es obligatorio'); return; }
    if (!firewall?.id) { setIfaceError('Guarda primero el firewall antes de añadir interfaces'); return; }
    try {
      setIfaceSaving(true); setIfaceError(null);
      let res;
      if (editingIface) {
        res = await api.updateFirewallInterface(firewall.id, editingIface.id, ifaceForm, companyId);
      } else {
        res = await api.addFirewallInterface(firewall.id, ifaceForm, companyId);
      }
      setIfaces(res.data?.interfaces || []);
      setShowIfaceForm(false);
      setEditingIface(null);
    } catch (e) { setIfaceError(e.message); }
    finally { setIfaceSaving(false); }
  };

  const handleIfaceDelete = async (iface) => {
    if (!window.confirm(`¿Eliminar interfaz "${iface.name}"?`)) return;
    try {
      await api.deleteFirewallInterface(firewall.id, iface.id, companyId);
      setIfaces(prev => prev.filter(i => i.id !== iface.id));
    } catch (e) { setError(e.message); }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const statusInfo  = STATUS_COLORS[form.status] || STATUS_COLORS.active;
  const ifaceCount  = ifaces.length;
  const vendorLabel = form.vendor || '—';
  const filteredModelOptions = useMemo(() => {
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.vendor || '').toLowerCase());
    const mid = matchedMfr?.id;
    return (modelOptions || []).filter(m => !mid || m.manufacturer_id === mid || !m.manufacturer_id);
  }, [modelOptions, manufacturerOptions, form.vendor]);
  const applianceLabel = APPLIANCES.find(a => a.value === form.appliance_type)?.label || '';

  // ── Catálogo de marcas / modelos ───────────────────────────────────────────
  const saveManufacturerToCatalog = async (name) => {
    const value = (name || '').trim();
    if (!value || !companyId) return;
    setCatalogSaving(true);
    setCatalogError(null);
    try {
      const exists = manufacturerOptions.some(
        m => (m.name || '').toLowerCase() === value.toLowerCase()
      );
      if (!exists) {
        await api.createManufacturer({ name: value, device_type: 'network' }, companyId);
      }
      await loadAux();
    } catch (e) {
      setCatalogError(e.message || 'No se pudo dar de alta la marca');
    } finally {
      setCatalogSaving(false);
    }
  };

  const saveModelToCatalog = async (modelName) => {
    const value = (modelName || '').trim();
    if (!value || !companyId) return;
    setCatalogSaving(true);
    setCatalogError(null);
    try {
      const matchedMfr = manufacturerOptions.find(
        m => (m.name || '').toLowerCase() === (form.vendor || '').toLowerCase()
      );
      await api.createDeviceModel(
        { name: value, device_type: 'network', manufacturer_id: matchedMfr?.id },
        companyId
      );
      await loadAux();
    } catch (e) {
      setCatalogError(e.message || 'No se pudo dar de alta el modelo');
    } finally {
      setCatalogSaving(false);
    }
  };

  // ── Variante tarjetas ──
  const paneClass = `dv-tab-pane${isCards ? ' sf2-pane' : ''}`;
  // En tarjetas no hay pestañas «General» ni «Ubicación»: su contenido vive en Resumen.
  const tabsFw = isCards
    ? [
        { key: 'general',    label: 'General' },
        { key: 'interfaces', label: `Interfaces (${ifaceCount})` },
        { key: 'access',     label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` },
        { key: 'ipam',       label: 'IPAM / IPs' },
        ...(isEditing && form.deployment_type !== 'virtual' ? [{ key: 'power', label: 'Fuentes' }] : []),
      ]
    : [
        { key: 'general',    label: 'General' },
        ...(form.deployment_type !== 'virtual' ? [{ key: 'ubicacion', label: 'Ubicación' }] : []),
        { key: 'interfaces', label: `Interfaces (${ifaceCount})` },
        { key: 'access',     label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` },
        { key: 'ipam',       label: 'IPAM / IPs' },
        ...(isEditing && form.deployment_type !== 'virtual' ? [{ key: 'power', label: 'Fuentes' }] : []),
      ];

  /* Ubicación compartida: la pestaña de la ficha clásica y la columna derecha
     de General en tarjetas pintan el mismo bloque (§12.C4 y C15). */
  const firewallUbicacionContent = (
    <>
      <div className="dv-section-header">Factor de forma</div>
      <div className="dv-field-grid" style={{ marginBottom: 24 }}>
        <div className="dv-field-group">
          <label className="dv-label">Factor de forma *</label>
          <select className="dv-select" value={form.form_factor} onChange={e => sf('form_factor', e.target.value)}>
            <option value="rack">Rack (en rack)</option>
            <option value="desktop">Sobremesa / Desktop</option>
            <option value="tower">Torre</option>
            <option value="wallmount">Montaje en pared</option>
          </select>
        </div>
        <div className="dv-field-group">
          <label className="dv-label">Altura (U)</label>
          <input
            className="dv-input"
            type="number" min="1" max="42"
            value={form.size_u}
            onChange={e => { setForm(f => ({ ...f, size_u: e.target.value, position_u: '' })); setIsDirty(true); }}
            disabled={form.form_factor !== 'rack'}
            placeholder="1"
          />
          {form.form_factor !== 'rack' && (
            <small style={{ color: '#94a3b8', fontSize: '0.75rem' }}>No aplica para factor de forma no-rack.</small>
          )}
        </div>
      </div>

      <div className="dv-section-header">Localización</div>
      <div className="dv-field-grid">
        <div className="dv-field-group">
          <label className="dv-label">Sede / Localización</label>
          <LocationTreeSelect
            locations={locations}
            value={form.location_id}
            onChange={(id) => handleLocationChange({ target: { value: id } })}
            placeholder="— Sin localización —"
          />
        </div>
        <div className="dv-field-group">
          <label className="dv-label">Rack</label>
          <select
            className="dv-select"
            value={form.rack_id}
            onChange={handleRackChange}
            disabled={form.form_factor !== 'rack'}
          >
            <option value="">— Sin rack —</option>
            {filteredRacks.map(r => (
              <option key={r.id} value={r.id}>
                {r.name}{r.location_name ? ` (${r.location_name})` : ''}
              </option>
            ))}
          </select>
          {form.form_factor !== 'rack' && (
            <small style={{ color: '#94a3b8', fontSize: '0.75rem' }}>No aplica — factor de forma no-rack.</small>
          )}
        </div>
        {form.form_factor === 'rack' && form.rack_id && (
          <div className="dv-field-group">
            <label className="dv-label">Posición U (inicio)</label>
            {validPositions !== null ? (
              validPositions.length === 0 ? (
                <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '4px 0' }}>
                  ⚠ Sin posiciones libres para {form.size_u}U en este rack.
                </p>
              ) : (
                <select
                  className="dv-select"
                  value={form.position_u}
                  onChange={e => sf('position_u', e.target.value)}
                >
                  <option value="">— Seleccionar posición libre —</option>
                  {validPositions.map(u => (
                    <option key={u} value={u}>
                      U{u} – U{u + Number(form.size_u) - 1}
                    </option>
                  ))}
                </select>
              )
            ) : (
              <input
                className="dv-input"
                type="number" min="1" max="42"
                value={form.position_u}
                onChange={e => sf('position_u', e.target.value)}
                placeholder="1"
              />
            )}
            {rangeLabel && (
              <small style={{ color: '#0369a1', fontSize: '0.75rem', fontWeight: 600 }}>
                Ocupa: {rangeLabel}
              </small>
            )}
          </div>
        )}
      </div>

      {form.location_name && (
        <div style={{ marginTop: 20, padding: '12px 16px', background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd', display: 'flex', alignItems: 'center', gap: 10 }}>
          <MapPinIcon style={{ width: 16, height: 16, color: '#0369a1', flexShrink: 0 }} />
          <div style={{ fontSize: '0.82rem', color: '#0369a1' }}>
            <strong>{form.location_name}</strong>
            {form.rack_name && <span> · Rack <strong>{form.rack_name}</strong></span>}
            {rangeLabel && <span> · {rangeLabel}</span>}
            {form.form_factor !== 'rack' && <span> · {form.form_factor}</span>}
          </div>
        </div>
      )}
    </>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="dv-overlay">
      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon />
          Firewalls
        </button>
        <DetailCrumb label={form.name || 'Nuevo firewall'} />

        {/* ── Navegación entre fichas: a la izquierda del spacer ── */}
        <FormPager
          items={navItems}
          index={navIndex}
          label="Firewall"
          disabled={saving}
          onChange={irAFirewall}
        />
        {navGuard.dialog}

        <div className="dv-topbar-spacer" />
        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>✓ Guardado</span>
        )}
        {!isCards && (
        <span
          className="dv-status-badge"
          style={{ background: statusInfo.bg, color: statusInfo.color, cursor: editing ? 'pointer' : 'default' }}
          title={editing ? 'Pulsar para cambiar el estado' : undefined}
          onClick={editing
            ? () => {
              const statuses = ['active', 'standby', 'maintenance', 'decom'];
              const idx = statuses.indexOf(form.status);
              sf('status', statuses[(idx + 1) % statuses.length]);
            }
            : undefined}
        >
          {statusInfo.label}
        </span>
        )}
        <div className="dv-topbar-actions">
          {error && <span style={{ color: '#dc2626', fontSize: '0.82rem' }}>{error}</span>}
          {isCards ? (
            <>
              <button className="dv-btn-discard" onClick={onClose}>
                <XMarkIcon style={{ width: 15, height: 15 }} /> Cerrar
              </button>
              {isEditing && (
                <button className="dv-btn-secondary" onClick={openCloneModal} title="Clonar este firewall con toda su configuración">
                  <DocumentDuplicateIcon style={{ width: 15, height: 15 }} /> Copiar
                </button>
              )}
            </>
          ) : editing ? (
            <>
              <button className="dv-btn-discard" onClick={handleDiscard}>
                <XMarkIcon style={{ width: 15, height: 15 }} /> Descartar
              </button>
              <button className="dv-btn-save" onClick={handleSave} disabled={saving}>
                <CheckIcon style={{ width: 15, height: 15 }} />
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
              {isEditing && (
                <button className="dv-btn-secondary" onClick={openCloneModal} title="Clonar este firewall con toda su configuración">
                  <DocumentDuplicateIcon style={{ width: 15, height: 15 }} /> Copiar
                </button>
              )}
            </>
          ) : (
            <>
              <button className="dv-btn-discard" onClick={onClose}>
                <XMarkIcon style={{ width: 15, height: 15 }} /> Cerrar
              </button>
              <button className="dv-btn-save" onClick={() => setEditing(true)} disabled={saving}>
                <PencilSquareIcon style={{ width: 15, height: 15 }} /> Editar
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Clone Modal ── */}
      <Modal show={showCloneModal} onHide={() => setShowCloneModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <span style={{ marginRight: 8 }}>📋</span>
            Clonar firewall
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BsForm.Group>
            <BsForm.Label className="fw-semibold">Nombre del clon</BsForm.Label>
            <BsForm.Control
              type="text"
              value={cloneName}
              onChange={e => setCloneName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCloneConfirm(); }}
              placeholder="Nombre del nuevo firewall…"
              autoFocus
            />
            <BsForm.Text className="text-muted">
              Se copiará toda la configuración (marca, modelo, appliance, HA/VPN, ubicación, interfaces con su tipo de conector
              y las fuentes de alimentación). No se copiará el número de serie, la MAC de los interfaces ni las IPs y conexiones.
            </BsForm.Text>
          </BsForm.Group>
          {cloneError && <div className="alert alert-danger mt-3 mb-0 py-2">{cloneError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCloneModal(false)} disabled={cloning}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleCloneConfirm} disabled={cloning || !cloneName.trim()}>
            {cloning ? '⏳ Clonando…' : '📋 Clonar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        {/* Vendor */}
        <div className="dv-smart-btn">
          <span className="dv-smart-btn-icon">
            <ShieldCheckIcon style={{ width: 18, height: 18, color: '#2563eb' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {vendorLabel}
          </span>
          <span className="dv-smart-btn-label">Vendor</span>
        </div>

        <div className="dv-smart-sep" />

        {/* Appliance */}
        <div className="dv-smart-btn">
          <span className="dv-smart-btn-icon">
            <LockClosedIcon style={{ width: 18, height: 18, color: form.appliance_type !== 'none' ? '#16a34a' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {form.appliance_type !== 'none' ? applianceLabel : '—'}
          </span>
          <span className="dv-smart-btn-label">Appliance</span>
        </div>

        <div className="dv-smart-sep" />

        {/* Interfaces */}
        <div className="dv-smart-btn" onClick={() => setTab('interfaces')} style={{ cursor: 'pointer' }}>
          <span className="dv-smart-btn-icon">
            <WifiIcon style={{ width: 18, height: 18, color: ifaceCount > 0 ? '#0369a1' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value">{ifaceCount}</span>
          <span className="dv-smart-btn-label">Interfaces</span>
        </div>

        <div className="dv-smart-sep" />

        {/* HA */}
        <div className="dv-smart-btn">
          <span className="dv-smart-btn-icon">
            <GlobeAltIcon style={{ width: 18, height: 18, color: form.ha_enabled ? '#7c3aed' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem' }}>
            {form.ha_enabled
              ? (form.ha_role === 'primary' ? 'Primario' : form.ha_role === 'secondary' ? 'Secundario' : 'HA')
              : '—'}
          </span>
          <span className="dv-smart-btn-label">Alta disp.</span>
        </div>

        {form.deployment_type !== 'virtual' && (<>
          <div className="dv-smart-sep" />

          {/* Location */}
          <div className="dv-smart-btn" onClick={() => setTab('ubicacion')} style={{ cursor: 'pointer' }}>
            <span className="dv-smart-btn-icon">
              <MapPinIcon style={{ width: 18, height: 18, color: form.location_name ? '#0369a1' : '#94a3b8' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {form.location_name || '—'}
            </span>
            <span className="dv-smart-btn-label">Ubicación</span>
          </div>

          <div className="dv-smart-sep" />

          {/* Rack */}
          <div className="dv-smart-btn" onClick={() => setTab('ubicacion')} style={{ cursor: 'pointer' }}>
            <span className="dv-smart-btn-icon">
              <ServerStackIcon style={{ width: 18, height: 18, color: form.rack_name ? '#15803d' : '#94a3b8' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {form.rack_name || (form.form_factor !== 'rack' ? form.form_factor : '—')}
            </span>
            <span className="dv-smart-btn-label">Rack / Factor</span>
          </div>
        </>)}

        {firewall?.id && (
          <DocSmartButton elementType="firewall" elementId={firewall.id} elementName={form.name || firewall.name} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── */}
      <div className="dv-body">
        {/* En LECTURA se apaga todo el cuerpo de golpe con un solo `<fieldset
            disabled>` en vez de `disabled` campo a campo. En tarjetas el
            fieldset exterior queda activo y el bloqueo lo hace el interior. */}
        <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>
        <div className={`dv-main${isCards ? ' sf2-main' : ''}`}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<ShieldCheckIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(v) => sf('name', v)}
                placeholder="Nombre del firewall…"
                editing={editing}
                saving={saving}
                onSave={handleSave}
                onCancel={handleDiscard}
                onEdit={() => setEditing(true)}
                crumb={[vendorLabel, form.model, form.hostname, form.location_name].filter(Boolean).join(' › ')}
                badges={[
                  { text: statusInfo.label, color: form.status === 'active' ? '#16a34a' : form.status === 'standby' ? '#0369a1' : form.status === 'maintenance' ? '#f59e0b' : '#dc2626' },
                  ...(form.appliance_type !== 'none' ? [{ text: applianceLabel }] : []),
                  ...(form.ha_enabled ? [{ text: form.ha_role === 'primary' ? 'HA primario' : form.ha_role === 'secondary' ? 'HA secundario' : 'HA' }] : []),
                ]}
                chips={[
                  { value: ifaceCount, label: 'interfaces', onClick: () => setTab('interfaces') },
                  ...(accessCount > 0 ? [{ value: accessCount, label: 'accesos', onClick: () => setTab('access') }] : []),
                  ...(form.form_factor && form.deployment_type !== 'virtual' ? [{ value: form.form_factor, label: 'formato' }] : []),
                  ...(form.rack_name ? [{ value: form.rack_name, label: 'rack' }] : []),
                ]}
                extra={firewall?.id ? (
                  <DocSmartButton elementType="firewall" elementId={firewall.id} elementName={form.name || firewall.name} companyId={companyId} variant="chip" />
                ) : null}
              />
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    className="dv-record-title"
                    style={{ flex: 1 }}
                    value={form.name}
                    onChange={e => sf('name', e.target.value)}
                    placeholder="Nombre del firewall…"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleSuggest}
                    disabled={suggesting}
                    title="Sugerir nombre automático"
                    style={{ flexShrink: 0, padding: '4px 10px' }}
                  >
                    {suggesting ? '…' : <SparklesIcon style={{ width: 16, height: 16 }} />}
                  </button>
                </div>
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
                {tabsFw.map(t => (
                  <TabButton
                    key={t.key}
                    className={`dv-tab-btn${tab === t.key ? ' active' : ''}`}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                  </TabButton>
                ))}
              </div>
            )}
          >
          <fieldset className="dv-fieldset" disabled={!editing}>

          {/* ── Tab: General / Resumen ── */}
          {tab === 'general' && (
            <div className={paneClass}>
            {isCards && (
              <div className="sf2-hl" style={{ marginBottom: 12 }}>
                <div className="sf2-kpi"><span>Plataforma</span><b style={{ fontSize: '0.95rem' }}>{vendorLabel}</b></div>
                <div className="sf2-kpi"><span>Appliance</span><b style={{ fontSize: '0.95rem' }}>{form.appliance_type !== 'none' ? applianceLabel : '—'}</b></div>
                <div className="sf2-kpi"><span>Interfaces</span><b>{ifaceCount}</b></div>
                <div className="sf2-kpi"><span>Alta disp.</span><b style={{ fontSize: '0.95rem' }}>{form.ha_enabled ? (form.ha_role === 'primary' ? 'Primario' : form.ha_role === 'secondary' ? 'Secundario' : 'HA') : '—'}</b></div>
                <div className="sf2-kpi"><span>Estado</span><b style={{ fontSize: '0.95rem' }}>{statusInfo.label}</b></div>
              </div>
            )}
            <div className={isCards ? 'sf2-grid sf2-grid--2' : undefined}>
            <Sf2Card isCards={isCards} title="Identificación y despliegue" editing={editing}>
            <div className="dv-field-grid">

              {/* Tipo de despliegue */}
              <div className="dv-field-group">
                <label className="dv-label">Tipo de despliegue</label>
                <select
                  className="dv-select"
                  value={form.deployment_type}
                  onChange={e => {
                    sf('deployment_type', e.target.value);
                    if (e.target.value === 'physical') sf('vm_id', '');
                  }}
                >
                  {DEPLOYMENT_TYPES.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>

              {/* VM enlazada (solo si virtual) */}
              {form.deployment_type === 'virtual' && (
                <div className="dv-field-group">
                  <label className="dv-label">Máquina virtual</label>
                  <select
                    className="dv-select"
                    value={form.vm_id}
                    onChange={e => sf('vm_id', e.target.value)}
                  >
                    <option value="">— Selecciona una VM —</option>
                    {vmOptions.map(vm => (
                      <option key={vm.id} value={vm.id}>
                        {vm.name}{vm.fqdn ? ` (${vm.fqdn})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Nombre */}
              <div className="dv-field-group">
                <label className="dv-label">Nombre *</label>
                <input
                  className="dv-input"
                  value={form.name}
                  onChange={e => sf('name', e.target.value)}
                  placeholder="Ej. FW-PERIMETRAL-01"
                />
              </div>

              {/* Hostname */}
              <div className="dv-field-group">
                <label className="dv-label">Hostname / FQDN</label>
                <input
                  className="dv-input"
                  value={form.hostname}
                  onChange={e => sf('hostname', e.target.value)}
                  placeholder="fw-01.empresa.local"
                />
              </div>

              {/* Vendor */}
              <div className="dv-field-group">
                <label className="dv-label">Fabricante / Plataforma</label>
                <div className="d-flex gap-1">
                  <select
                    className="dv-select"
                    style={{ flex: '1 1 auto', minWidth: 0 }}
                    value={form.vendor}
                    onChange={e => { sf('vendor', e.target.value); setAddingManufacturer(false); setNewManufacturer(''); }}
                  >
                    <option value="">— Sin fabricante —</option>
                    {manufacturerOptions.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                    {form.vendor && !manufacturerOptions.find(m => m.name === form.vendor) && (
                      <option value={form.vendor}>{form.vendor}</option>
                    )}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm text-nowrap"
                    title="Dar de alta una marca en el maestro"
                    onClick={() => { setAddingManufacturer(v => !v); setCatalogError(null); }}
                  >
                    + Marca
                  </button>
                </div>
                {addingManufacturer && (
                  <div className="d-flex gap-1 mt-2">
                    <input
                      className="dv-input"
                      style={{ minWidth: 0 }}
                      placeholder="Nueva marca (ej. Fortinet)"
                      value={newManufacturer}
                      onChange={e => setNewManufacturer(e.target.value)}
                      onKeyDown={async e => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        const name = newManufacturer.trim();
                        if (!name) return;
                        sf('vendor', name);
                        await saveManufacturerToCatalog(name);
                        setNewManufacturer('');
                        setAddingManufacturer(false);
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={catalogSaving || !newManufacturer.trim()}
                      onClick={async () => {
                        const name = newManufacturer.trim();
                        if (!name) return;
                        sf('vendor', name);
                        await saveManufacturerToCatalog(name);
                        setNewManufacturer('');
                        setAddingManufacturer(false);
                      }}
                    >
                      Guardar
                    </button>
                  </div>
                )}
              </div>

              {/* Model */}
              <div className="dv-field-group">
                <label className="dv-label">Modelo</label>
                <div className="d-flex gap-1">
                  <select
                    className="dv-select"
                    style={{ flex: '1 1 auto', minWidth: 0 }}
                    value={form.model}
                    onChange={e => { sf('model', e.target.value); setAddingModel(false); setNewModel(''); }}
                  >
                    <option value="">— Sin modelo —</option>
                    {filteredModelOptions.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                    {form.model && !modelOptions.find(m => m.name === form.model) && (
                      <option value={form.model}>{form.model}</option>
                    )}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm text-nowrap"
                    title="Dar de alta un modelo en el maestro"
                    onClick={() => { setAddingModel(v => !v); setCatalogError(null); }}
                  >
                    + Modelo
                  </button>
                </div>
                {addingModel && (
                  <div className="d-flex gap-1 mt-2">
                    <input
                      className="dv-input"
                      style={{ minWidth: 0 }}
                      placeholder={form.vendor ? `Nuevo modelo de ${form.vendor}` : 'Nuevo modelo'}
                      value={newModel}
                      onChange={e => setNewModel(e.target.value)}
                      onKeyDown={async e => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        const name = newModel.trim();
                        if (!name) return;
                        sf('model', name);
                        await saveModelToCatalog(name);
                        setNewModel('');
                        setAddingModel(false);
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={catalogSaving || !newModel.trim()}
                      onClick={async () => {
                        const name = newModel.trim();
                        if (!name) return;
                        sf('model', name);
                        await saveModelToCatalog(name);
                        setNewModel('');
                        setAddingModel(false);
                      }}
                    >
                      Guardar
                    </button>
                  </div>
                )}
                {addingModel && !form.vendor && (
                  <small className="text-muted d-block mt-1">
                    Elige primero el fabricante para asociar el modelo a esa marca.
                  </small>
                )}
                {catalogError && (
                  <small className="text-danger d-block mt-1">{catalogError}</small>
                )}
              </div>

              {/* Appliance */}
              <div className="dv-field-group">
                <label className="dv-label">Appliance software</label>
                <select className="dv-select" value={form.appliance_type} onChange={e => sf('appliance_type', e.target.value)}>
                  {APPLIANCES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>

              {/* Appliance ver */}
              <div className="dv-field-group">
                <label className="dv-label">Versión appliance</label>
                <input
                  className="dv-input"
                  value={form.appliance_version}
                  onChange={e => sf('appliance_version', e.target.value)}
                  placeholder="2.7.2, 23.7.10…"
                  disabled={form.appliance_type === 'none'}
                />
              </div>

              {/* Firmware */}
              <div className="dv-field-group">
                <label className="dv-label">Versión firmware / OS</label>
                <input
                  className="dv-input"
                  value={form.firmware_version}
                  onChange={e => sf('firmware_version', e.target.value)}
                  placeholder="9.16.4, 7.0.5…"
                />
              </div>

              {/* Throughput */}
              <div className="dv-field-group">
                <label className="dv-label">Throughput (Gbps)</label>
                <input
                  className="dv-input"
                  type="number"
                  min="0"
                  step="0.1"
                  value={form.throughput_gbps}
                  onChange={e => sf('throughput_gbps', e.target.value)}
                  placeholder="1.0"
                />
              </div>

              {/* Serial */}
              <div className="dv-field-group">
                <label className="dv-label">Número de serie</label>
                <input
                  className="dv-input"
                  value={form.serial_number}
                  onChange={e => sf('serial_number', e.target.value)}
                />
              </div>

              {/* Asset tag */}
              <div className="dv-field-group">
                <label className="dv-label">Asset tag</label>
                <input
                  className="dv-input"
                  value={form.asset_tag}
                  onChange={e => sf('asset_tag', e.target.value)}
                />
              </div>

              {/* Estado */}
              <div className="dv-field-group">
                <label className="dv-label">Estado</label>
                <select className="dv-select" value={form.status} onChange={e => sf('status', e.target.value)}>
                  <option value="active">Activo</option>
                  <option value="standby">Standby</option>
                  <option value="maintenance">Mantenimiento</option>
                  <option value="decom">Decommissioned</option>
                </select>
              </div>

            </div>

            {/* Capabilities section */}
            <div className="dv-section-header" style={{ marginTop: 28 }}>Capacidades</div>
            <div className="dv-field-grid">

              {/* HA enabled */}
              <div className="dv-field-group">
                <label className="dv-label">Alta disponibilidad</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                  <input
                    type="checkbox"
                    id="ha_enabled"
                    checked={form.ha_enabled}
                    onChange={e => sf('ha_enabled', e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  <label htmlFor="ha_enabled" style={{ fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>
                    Habilitada
                  </label>
                </div>
              </div>

              {/* HA Role */}
              <div className="dv-field-group">
                <label className="dv-label">Rol en clúster HA</label>
                <select
                  className="dv-select"
                  value={form.ha_role}
                  onChange={e => sf('ha_role', e.target.value)}
                  disabled={!form.ha_enabled}
                >
                  <option value="standalone">Standalone (sin HA)</option>
                  <option value="primary">Primario (activo)</option>
                  <option value="secondary">Secundario (pasivo)</option>
                </select>
              </div>

              {/* IDS/IPS */}
              <div className="dv-field-group">
                <label className="dv-label">IDS / IPS</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                  <input
                    type="checkbox"
                    id="ids_ips"
                    checked={form.ids_ips}
                    onChange={e => sf('ids_ips', e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  <label htmlFor="ids_ips" style={{ fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>
                    Habilitado
                  </label>
                </div>
              </div>

              {/* VPN */}
              <div className="dv-field-group">
                <label className="dv-label">VPN</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                  <input
                    type="checkbox"
                    id="vpn_capable"
                    checked={form.vpn_capable}
                    onChange={e => sf('vpn_capable', e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  <label htmlFor="vpn_capable" style={{ fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>
                    Habilitada
                  </label>
                </div>
              </div>

            </div>
            </Sf2Card>
            {isCards && form.deployment_type !== 'virtual' && (
              <Sf2Card isCards title="Ubicación" editing={editing}>{firewallUbicacionContent}</Sf2Card>
            )}
            </div>
            </div>
          )}

          {/* ── Tab: Ubicación (solo ficha clásica; en tarjetas va a la derecha de General) ── */}
          {!isCards && tab === 'ubicacion' && form.deployment_type !== 'virtual' && (
            <div className={paneClass}>
              <Sf2Card isCards={isCards} title="Ubicación física" editing={editing}>
                {firewallUbicacionContent}
              </Sf2Card>
            </div>
          )}

          {/* ── Tab: Interfaces ── */}
          {tab === 'interfaces' && (
            <div className={paneClass}>
            {/* Interface add form */}
            {showIfaceForm && (
              <div style={{
                background: 'var(--bg-toolbar)', border: '1px solid var(--border)', borderRadius: 8,
                padding: '18px 20px', marginBottom: 20
              }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 14 }}>
                  {editingIface ? 'Editar interfaz' : 'Nuevo interfaz'}
                </div>
                <div className="dv-field-grid">
                  <div className="dv-field-group">
                    <label className="dv-label">Nombre *</label>
                    <input
                      className="dv-input"
                      value={ifaceForm.name}
                      onChange={e => setIfaceForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="WAN, LAN, DMZ, em0…"
                    />
                  </div>
                  <div className="dv-field-group">
                    <label className="dv-label">Tipo / Rol</label>
                    <select
                      className="dv-select"
                      value={ifaceForm.iface_type}
                      onChange={e => setIfaceForm(f => ({ ...f, iface_type: e.target.value }))}
                    >
                      {IFACE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="dv-field-group">
                    <label className="dv-label">Tipo de conector</label>
                    <select
                      className="dv-select"
                      value={ifaceForm.connector_type}
                      onChange={e => setIfaceForm(f => ({ ...f, connector_type: e.target.value }))}
                    >
                      {IFACE_CONNECTORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="dv-field-group">
                    <label className="dv-label">MAC Address</label>
                    <input
                      className="dv-input"
                      value={ifaceForm.mac_address}
                      onChange={e => setIfaceForm(f => ({ ...f, mac_address: e.target.value }))}
                      placeholder="aa:bb:cc:dd:ee:ff"
                    />
                  </div>
                  <div className="dv-field-group">
                    <label className="dv-label">Velocidad (Mbps)</label>
                    <input
                      className="dv-input"
                      type="number"
                      value={ifaceForm.speed_mbps}
                      onChange={e => setIfaceForm(f => ({ ...f, speed_mbps: e.target.value }))}
                      placeholder="1000"
                    />
                  </div>
                  <div className="dv-field-group">
                    <label className="dv-label">VLAN ID</label>
                    <input
                      className="dv-input"
                      type="number"
                      value={ifaceForm.vlan_id}
                      onChange={e => setIfaceForm(f => ({ ...f, vlan_id: e.target.value }))}
                      placeholder="10"
                    />
                  </div>
                  <div className="dv-field-group">
                    <label className="dv-label">Descripción</label>
                    <input
                      className="dv-input"
                      value={ifaceForm.description}
                      onChange={e => setIfaceForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Descripción opcional"
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <input
                    type="checkbox"
                    id="iface_enabled"
                    checked={ifaceForm.enabled}
                    onChange={e => setIfaceForm(f => ({ ...f, enabled: e.target.checked }))}
                  />
                  <label htmlFor="iface_enabled" style={{ fontSize: '0.82rem', color: '#334155', cursor: 'pointer' }}>
                    Interfaz habilitado
                  </label>
                </div>
                {ifaceError && (
                  <div style={{ color: '#dc2626', fontSize: '0.82rem', marginTop: 8 }}>{ifaceError}</div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button className="dv-btn-save" onClick={handleIfaceSave} disabled={ifaceSaving}>
                    <CheckIcon style={{ width: 14, height: 14 }} />
                    {ifaceSaving ? 'Guardando…' : editingIface ? 'Actualizar' : 'Añadir'}
                  </button>
                  <button className="dv-btn-discard" onClick={() => { setShowIfaceForm(false); setEditingIface(null); }}>
                    <XMarkIcon style={{ width: 14, height: 14 }} /> Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Add button */}
            {!showIfaceForm && (
              <div style={{ marginBottom: 16 }}>
                {!isEditing && (
                  <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 8 }}>
                    Guarda el firewall primero para poder añadir interfaces.
                  </p>
                )}
                <button
                  className="dv-btn-save"
                  onClick={openNewIface}
                  disabled={!isEditing}
                  style={{ opacity: isEditing ? 1 : 0.5 }}
                >
                  <PlusIcon style={{ width: 15, height: 15 }} /> Añadir interfaz
                </button>
              </div>
            )}

            {/* Interface table */}
            {ifaces.length === 0 ? (
              <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>
                Sin interfaces configurados. Añade al menos WAN y LAN.
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Nombre</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Tipo / Rol</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Conector</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>MAC</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Velocidad</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>VLAN</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Estado</th>
                    <th style={{ padding: '6px 10px' }} />
                  </tr>
                </thead>
                <tbody>
                  {ifaces.map(iface => {
                    const tc = IFACE_TYPE_COLOR[iface.iface_type] || IFACE_TYPE_COLOR.other;
                    return (
                      <tr key={iface.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 10px', fontWeight: 600 }}>{iface.name}</td>
                        <td style={{ padding: '8px 10px' }}>
                          <span style={{
                            background: tc.bg, color: tc.color,
                            borderRadius: 4, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 600
                          }}>
                            {iface.iface_type_label || iface.iface_type}
                          </span>
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          {iface.connector_type ? (
                            <span style={{
                              background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0',
                              borderRadius: 4, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 600
                            }}>
                              {IFACE_CONNECTOR_SHORT[iface.connector_type] || iface.connector_type}
                            </span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: '0.8rem', color: '#475569' }}>
                          {iface.mac_address || '—'}
                        </td>
                        <td style={{ padding: '8px 10px', color: '#475569' }}>
                          {iface.speed_mbps ? `${iface.speed_mbps} Mbps` : '—'}
                        </td>
                        <td style={{ padding: '8px 10px', color: '#475569' }}>
                          {iface.vlan_id || '—'}
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          {iface.enabled
                            ? <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>● Activo</span>
                            : <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>○ Desactivado</span>
                          }
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb' }}
                              onClick={() => openEditIface(iface)}
                              title="Editar"
                            >
                              <PencilSquareIcon style={{ width: 16, height: 16 }} />
                            </button>
                            <button
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                              onClick={() => handleIfaceDelete(iface)}
                              title="Eliminar"
                            >
                              <TrashIcon style={{ width: 16, height: 16 }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Tip about IPAM */}
            {ifaces.length > 0 && (
              <p style={{ marginTop: 16, fontSize: '0.78rem', color: '#64748b' }}>
                💡 Para asignar IPs a cada interfaz, ve a la pestaña <strong>IPAM / IPs</strong> y usa el campo <em>Interfaz ref.</em> con el nombre del interfaz (e.g. WAN, LAN).
              </p>
            )}
          </div>
        )}

          {/* ── Tab: IPAM ── */}
          {tab === 'ipam' && (
            <div className={paneClass}>
            {isEditing ? (
              <>
                <ElementIpPanel
                  objectType="firewall"
                  objectId={firewall.id}
                  companyId={companyId}
                  elementName={form.name}
                  interfaces={ifaces}
                />
                <NetworkConnectionsPanel
                  objectType="firewall"
                  objectId={firewall.id}
                  companyId={companyId}
                  elementName={form.name}
                />
              </>
            ) : (
              <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>
                Guarda el firewall primero para gestionar sus IPs.
              </p>
            )}
          </div>
        )}

          {/* ── Tab: Accesos ── */}
          {tab === 'access' && (
            <div className={paneClass}>
              {isEditing ? (
                <DeviceAccessPanel
                  objectType="firewall"
                  objectId={firewall?.id}
                  companyId={companyId}
                  elementName={form.name}
                  onCountChange={setAccessCount}
                  canEdit={editing}
                />
              ) : (
                <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>
                  Guarda el firewall primero para documentar sus accesos.
                </p>
              )}
            </div>
          )}

          {/* ── Tab: Fuentes / PDU ── */}
          {tab === 'power' && isEditing && form.deployment_type !== 'virtual' && (
            <div className={paneClass}>
              {!firewall.rack_element_id ? (
                <div className="dv-section">
                  <p className="dv-hint">Asocia primero el firewall a un rack para gestionar sus fuentes y conexiones PDU.</p>
                </div>
              ) : (
                <>
                  <div className="dv-section">
                    <SwitchPsuSection elementId={firewall.rack_element_id} companyId={companyId} />
                  </div>
                  <div className="dv-section" style={{ marginTop: 24 }}>
                    <div className="dv-section-title">Conexiones PDU / Alimentación eléctrica</div>
                    <PowerConnectionsPanel
                      consumerType="rack_element"
                      consumerId={firewall.rack_element_id}
                      companyId={companyId}
                      rackId={undefined}
                      elementName={form.name}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          </fieldset>
          <FormDirtyDock
            show={isCards && isDirty}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={handleSave}
          />
          </FormCardsShell>
        </div>
        </fieldset>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="firewall"
          ciId={firewall?.id}
          tipoLegible="el firewall"
        />
      </div>
    </div>
  );
}
